import { useState } from "react";
import { Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Product gallery for /shop/:slug — big hero image + thumbnail strip.
 * Mobile: swipe-friendly with thumb strip below. Desktop: fixed hero
 * with vertical / horizontal thumb strip.
 *
 * Accepts the resolved variant's `hero_image` + `gallery`.
 */
export default function ProductGallery({ hero, gallery = [], productName = "" }) {
  const slides = [];
  if (hero?.url) slides.push(hero);
  for (const g of gallery || []) {
    if (g?.url && !slides.find((s) => s.url === g.url)) slides.push(g);
  }
  const [active, setActive] = useState(0);

  if (slides.length === 0) {
    return (
      <div
        className="aspect-square bg-gradient-to-br from-emerald-50/60 to-amber-50/30 rounded-3xl flex items-center justify-center"
        data-testid="shop-gallery-empty"
      >
        <Sparkles className="w-10 h-10 text-emerald-700/30" strokeWidth={1.3} />
      </div>
    );
  }

  const cur = slides[active];
  const prev = () => setActive((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setActive((i) => (i + 1) % slides.length);

  return (
    <div data-testid="shop-product-gallery">
      <div className="relative aspect-square rounded-3xl bg-gradient-to-br from-emerald-50/40 via-amber-50/20 to-white overflow-hidden border border-emerald-100">
        <img
          key={cur.url}
          src={cur.url}
          alt={cur.alt || productName}
          loading="eager"
          className="w-full h-full object-cover animate-in fade-in duration-300"
          data-testid="shop-gallery-active-img"
        />
        {slides.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous image"
              className="absolute top-1/2 -translate-y-1/2 left-3 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm text-emerald-900 flex items-center justify-center hover:bg-white transition shadow"
              data-testid="shop-gallery-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next image"
              className="absolute top-1/2 -translate-y-1/2 right-3 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm text-emerald-900 flex items-center justify-center hover:bg-white transition shadow"
              data-testid="shop-gallery-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex gap-1.5">
              {slides.map((_, i) => (
                <span
                  key={i}
                  className={`block w-1.5 h-1.5 rounded-full transition ${i === active ? "bg-emerald-700 w-4" : "bg-white/70"}`}
                  aria-hidden
                />
              ))}
            </div>
          </>
        )}
      </div>

      {slides.length > 1 && (
        <ul className="mt-3 grid grid-cols-5 gap-2" data-testid="shop-gallery-thumbs">
          {slides.slice(0, 5).map((s, i) => (
            <li key={s.url + i}>
              <button
                type="button"
                onClick={() => setActive(i)}
                aria-label={`View image ${i + 1}`}
                className={`block aspect-square rounded-xl overflow-hidden border-2 transition ${i === active ? "border-emerald-700" : "border-transparent hover:border-emerald-200"}`}
              >
                <img src={s.url} alt="" loading="lazy" className="w-full h-full object-cover" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
